<template>
  <SloLoader :loading="paymentDetailLoading" size="xl">
    <article class="personal-details">
      <div class="pt-md px-md pb-0 sm:px-lg sm:pt-lg">
        <h3 id="payment-detail-form-header" class="text-xl fw-strong">
          {{ 'Update Payment Method' | t }}
        </h3>
      </div>
      <form @submit.prevent="handleSubmit" class="mt-lg">
        <div class="px-md sm:px-lg">
          <div class="update-payment-details-grid">
            <SloField>
              <SloLabel>{{ 'Name on card' | t }}</SloLabel>
              <SloInput readonly="readonly" :value="paymentDetail?.accountHolderName" />
            </SloField>
            <SloField>
              <SloLabel>{{ 'Credit card number' | t }}</SloLabel>
              <SloInput
                readonly="readonly"
                :value="paymentDetail?.cardNumber | getCardNumberLastFourDigits"
              />
            </SloField>
            <SloField>
              <SloLabel :error="isCardExpired">
                {{ 'Expiration Date (MM/YY)' | t }}
              </SloLabel>
              <div class="flex items-center gap-sm">
                <SloSelect
                  v-model="paymentDetail.expiryMonth"
                  :value="paymentDetail?.expiryMonth"
                  :error="isCardExpired"
                  name="expiryMonth"
                  class="flex-1"
                >
                  <option
                    v-for="(expiryMonthOption, index) in expiryMonthOptions"
                    :key="`expiry-month-${index}`"
                    :value="expiryMonthOption.value"
                    v-html="expiryMonthOption.label"
                  />
                </SloSelect>
                /
                <SloSelect
                  v-model="paymentDetail.expiryYear"
                  :value="paymentDetail?.expiryYear.toString()"
                  :error="isCardExpired"
                  name="expiryYear"
                  class="flex-1"
                >
                  <option
                    v-for="(expiryYearOption, index) in expiryYearOptions"
                    :key="`expiry-year-${index}`"
                    :value="expiryYearOption.value"
                    v-html="expiryYearOption.label"
                  />
                  <option
                    v-if="isCardExpired"
                    v-html="paymentDetail.expiryYear"
                    :key="`expiry-year-${paymentDetail.expiryYear}`"
                    :value="paymentDetail.expiryYear"
                  />
                </SloSelect>
              </div>
              <span v-if="isCardExpired" class="text-sm fw-book c-red-400">
                {{ 'Expiration date has expired, please update' | t }}
              </span>
            </SloField>
            <SloField>
              <SloLabel>{{ 'Security code (CVV)' | t }}</SloLabel>
              <SloInput readonly="readonly" value="•••" />
            </SloField>
          </div>
          <div class="flex flex-col my-lg">
            <h3 class="mb-md text-xl fw-strong">{{ 'Billing Address' | t }}</h3>
            <div class="flex flex-col pt-sm">
              <span class="c-gray-700">
                {{ paymentDetail?.billingAddress?.firstName }}
              </span>
              <span class="c-gray-700">
                {{ paymentDetail?.billingAddress?.line1 }}
              </span>
              <span class="c-gray-700" v-if="paymentDetail?.billingAddress?.line2">
                {{ paymentDetail?.billingAddress?.line2 }}
              </span>
              <span class="c-gray-700">
                {{ paymentDetail?.billingAddress?.town }},
                {{ paymentDetail?.billingAddress?.region?.isocodeShort || paymentDetail?.billingAddress?.region?.isocode }}
                {{ paymentDetail?.billingAddress?.postalCode }}
              </span>
              <span class="c-gray-700" v-if="paymentDetail?.billingAddress?.phone">
                {{ paymentDetail?.billingAddress?.phone }}
              </span>
            </div>
            <SloField class="self-start mt-md">
              <SloLabel class="flex items-center justify-start">
                <SloInput
                  v-model="paymentDetail.defaultPayment"
                  type="checkbox"
                  :value="true"
                  :checked="paymentDetail.defaultPayment"
                  class="mr-sm"
                />
                {{ 'Make Default' | t }}
              </SloLabel>
            </SloField>
          </div>
        </div>
        <div class="personal-details--footer flex justify-end gap-md px-md py-md bg-gray-100 sm:px-lg">
          <SloButton
            :disabled="loading"
            :to="{name: 'marketplace-account-payments'}"
            fill="outline"
          >
            {{ 'Cancel' | t }}
          </SloButton>
          <SloButton
            :disabled="loading || isCardExpired"
            type="submit"
            fill="solid"
            class="fixed-button-width"
          >
            <SloLoader :loading="loading" size="md" color="white">
              {{ 'Update' | t }}
            </SloLoader>
          </SloButton>
        </div>
      </form>
    </article>
  </SloLoader>
</template>

<script>
import t from '../../main/i18n';
import {marketplaceApi} from '../apis/marketplace';
import {
  getCardNumberLastFourDigits,
  formatSingleLineAddress,
  isCardExpired
} from '../../main/utils';
import SloButton from '../../../lib/components/SloButton.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';

export default {
  components: {
    SloButton,
    SloLoader,
    SloField,
    SloLabel,
    SloInput,
    SloSelect
  },
  filters: {
    t,
    getCardNumberLastFourDigits,
    formatSingleLineAddress
  },
  data() {
    return {
      loading: false,
      paymentDetailLoading: true,
      paymentDetail: {
        defaultPayment: false,
        expiryMonth: '',
        expiryYear: ''
      },
      expiryMonthOptions: [],
      expiryYearOptions: []
    };
  },
  async created() {
    this.expiryMonthOptions = [
      {label: '01', value: '01'},
      {label: '02', value: '02'},
      {label: '03', value: '03'},
      {label: '04', value: '04'},
      {label: '05', value: '05'},
      {label: '06', value: '06'},
      {label: '07', value: '07'},
      {label: '08', value: '08'},
      {label: '09', value: '09'},
      {label: '10', value: '10'},
      {label: '11', value: '11'},
      {label: '12', value: '12'}
    ];

    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i < currentYear + 10; i++) {
      this.expiryYearOptions.push({label: i, value: i});
    }

    await marketplaceApi.getCurrentUserPaymentDetail(this.$route.params.id)
      .then(res => {
        this.paymentDetail = res.data;
        this.paymentDetail.expiryMonth = res.data?.expiryMonth.toString().padStart(2, '0');
        this.paymentDetailLoading = false;
      })
      .catch(async() => {
        this.$snotify.error(t('Payment method not found'));
        await this.$router.push({name: 'marketplace-account-payments'});
      });
  },
  computed: {
    isCardExpired() {
      return isCardExpired(this.paymentDetail);
    }
  },
  methods: {
    async handleSubmit() {
      this.loading = true;
      await marketplaceApi.updateCurrentUserPaymentDetail(this.paymentDetail)
        .then(async() => {
          this.$snotify.success(t('Payment method updated'));
          await this.$router.push({name: 'marketplace-account-payments'});
        })
        .catch(() => {
          this.loading = false;
          this.$snotify.error(t('Error occurred while updating the payment method'));
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.personal-details--footer {
  border-radius: 0 0 1.2rem 1.2rem;
}
</style>
